import React, { useEffect, useMemo, useRef, useState } from 'react';
import axiosInstance from '../../../utils/axiosConfig';
import {
    Avatar, Cargando, EnVivo, NombrePersona, display, fechaCorta, fechaLarga, horaAmPm, horaDeIso, minutosTexto, panel,
} from '../ui';
import LlegadasFranja from './LlegadasFranja';

const REFRESCO_MS = 15000;
const VISIBLES = 8;

const aMinutos = (hhmm) => {
    const [h, m] = hhmm.split(':').map(Number);
    return h * 60 + m;
};

// Una lista corta con "ver todos"
const Lista = ({ titulo, cantidad, tono, vacio, children, total }) => {
    const [todos, setTodos] = useState(false);
    const filas = React.Children.toArray(children);
    return (
        <section className={`${panel} p-5 flex flex-col min-h-[12rem]`}>
            <header className="flex items-baseline justify-between gap-3 mb-3">
                <h2 className="text-sm font-semibold text-slate-900">{titulo}</h2>
                <span className={`${display} text-2xl font-semibold tabular-nums ${tono}`}>{cantidad}</span>
            </header>
            {filas.length === 0 ? (
                <p className="text-sm text-slate-400 my-auto">{vacio}</p>
            ) : (
                <ul className="space-y-2.5">{todos ? filas : filas.slice(0, VISIBLES)}</ul>
            )}
            {filas.length > VISIBLES && (
                <button
                    type="button" onClick={() => setTodos((t) => !t)}
                    className="mt-3 self-start text-sm font-medium text-sura-blue hover:underline focus-visible:outline-none focus-visible:underline"
                >
                    {todos ? 'Ver menos' : `Ver los ${total ?? filas.length}`}
                </button>
            )}
        </section>
    );
};

const Fila = ({ persona, detalle, derecha }) => (
    <li className="flex items-center gap-3 min-w-0">
        <Avatar persona={persona} size={32} />
        <div className="min-w-0 flex-1">
            <NombrePersona persona={persona} className="block text-sm font-medium text-slate-900 truncate max-w-full" />
            {detalle && <p className="text-xs text-slate-500 truncate">{detalle}</p>}
        </div>
        {derecha}
    </li>
);

const fueraTexto = (i) => {
    if (i.hora_inicio) return `${i.titulo}, de ${horaAmPm(i.hora_inicio)} a ${horaAmPm(i.hora_fin)}`;
    if (i.tipo === 'vacaciones') return `Vacaciones hasta el ${fechaCorta(i.hasta)}`;
    return i.desde === i.hasta ? i.titulo : `${i.titulo} hasta el ${fechaCorta(i.hasta)}`;
};

const HoyVista = () => {
    const [datos, setDatos] = useState(null);
    const [error, setError] = useState(null);
    const vistos = useRef(null);
    const [nuevos, setNuevos] = useState(new Set());

    useEffect(() => {
        let vivo = true;
        const cargar = async () => {
            try {
                const { data } = await axiosInstance.get('/api/asistencia/hoy');
                if (!vivo) return;
                const ids = new Set(data.recientes.map((m) => m.id));
                setNuevos(vistos.current ? new Set([...ids].filter((id) => !vistos.current.has(id))) : new Set());
                vistos.current = ids;
                setDatos(data);
                setError(null);
            } catch (e) {
                if (vivo) setError('No se pudo actualizar. Se reintenta solo en unos segundos.');
            }
        };
        cargar();
        const t = setInterval(cargar, REFRESCO_MS);
        return () => { vivo = false; clearInterval(t); };
    }, []);

    const resumen = useMemo(() => {
        if (!datos) return null;
        const ps = datos.personas;
        const marcaron = ps.filter((p) => p.entrada);
        const tarde = marcaron.filter((p) => p.retardo_min > 0).sort((a, b) => b.retardo_min - a.retardo_min);
        const fueraIds = new Set(datos.fuera.filter((f) => !f.hora_inicio).map((f) => f.empleado_id));
        const justificados = ps.filter((p) => !p.entrada && (p.estado === 'justificada' || fueraIds.has(p.empleado_id)));
        const sinMarcar = ps.filter((p) => !p.entrada && !justificados.includes(p) && p.estado !== 'libre');
        return {
            total: ps.length, marcaron, tarde, justificados, sinMarcar,
            aTiempo: marcaron.length - tarde.length,
        };
    }, [datos]);

    if (!datos) return error ? <p className="text-sm text-rose-600">{error}</p> : <Cargando alto="h-96" />;

    const { horario } = datos;
    const minAhora = aMinutos(datos.ahora.slice(11, 16));
    const yaEsHora = horario ? minAhora > aMinutos(horario.entrada) + (horario.tolerancia_min || 0) : true;
    const laborable = !!horario && !datos.feriado;
    const r = resumen;
    const segmentos = [
        { clave: 'a tiempo', valor: r.aTiempo, color: 'bg-[#1F4E8C]' },
        { clave: 'tarde', valor: r.tarde.length, color: 'bg-amber-500' },
        { clave: 'fuera', valor: r.justificados.length, color: 'bg-emerald-500' },
        { clave: 'sin marcar', valor: r.sinMarcar.length, color: yaEsHora ? 'bg-rose-400' : 'bg-slate-200' },
    ];

    return (
        <div className="space-y-6">
            {/* Encabezado del día */}
            <header className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <h1 className={`${display} text-3xl font-bold text-slate-900`}>{fechaLarga(datos.fecha)}</h1>
                    <p className="text-sm text-slate-500 mt-1">
                        {datos.feriado
                            ? `Hoy no se trabaja: ${datos.feriado}.`
                            : horario
                                ? `Horario ${horario.nombre}: de ${horaAmPm(horario.entrada)} a ${horaAmPm(horario.salida)}${horario.tolerancia_min ? `, con ${horario.tolerancia_min} min de tolerancia` : ''}`
                                : 'Hoy no es día laborable.'}
                    </p>
                </div>
                <EnVivo texto={`En vivo, actualizado a las ${horaDeIso(datos.ahora)}`} />
            </header>
            {error && <p className="text-sm text-rose-600">{error}</p>}

            {/* Cómo va el día */}
            <section className={`${panel} p-6`}>
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <p className={`${display} text-4xl font-bold text-sura-blue tabular-nums`}>{r.marcaron.length}</p>
                    <p className="text-base text-slate-600">
                        de {r.total} personas ya marcaron entrada
                    </p>
                </div>
                <div className="mt-4 flex h-3 rounded-full overflow-hidden bg-slate-100" role="img"
                    aria-label={segmentos.map((s) => `${s.valor} ${s.clave}`).join(', ')}>
                    {segmentos.filter((s) => s.valor > 0).map((s) => (
                        <div key={s.clave} className={`${s.color} h-full transition-[width] duration-700 first:rounded-l-full last:rounded-r-full`}
                            style={{ width: `${(s.valor / Math.max(r.total, 1)) * 100}%` }} />
                    ))}
                </div>
                <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
                    {segmentos.map((s) => (
                        <span key={s.clave} className="inline-flex items-center gap-2 text-slate-600">
                            <span className={`w-2.5 h-2.5 rounded-full ${s.color}`} />
                            <span className="font-semibold tabular-nums text-slate-900">{s.valor}</span>
                            {s.clave === 'sin marcar' && !yaEsHora ? 'todavía no llegan' : s.clave}
                        </span>
                    ))}
                </div>

                <div className="mt-8">
                    <div className="flex items-baseline justify-between gap-4 mb-4">
                        <h2 className="text-sm font-semibold text-slate-900">Llegadas de hoy</h2>
                        <p className="text-xs text-slate-400 hidden sm:block">Cada cara es una persona en la hora en que marcó. Toca una para ver su mes.</p>
                    </div>
                    {r.marcaron.length ? (
                        <LlegadasFranja personas={datos.personas} horario={horario} ahora={datos.ahora} esHoy />
                    ) : (
                        <p className="text-sm text-slate-400 py-10 text-center">
                            {laborable ? 'Todavía nadie ha marcado hoy.' : 'Nadie ha marcado hoy.'}
                        </p>
                    )}
                </div>
            </section>

            {/* Quién llegó tarde, quién falta, quién está fuera y lo último que marcó */}
            <div className="grid gap-6 md:grid-cols-2 2xl:grid-cols-4">
                <Lista titulo="Llegaron tarde" cantidad={r.tarde.length} tono="text-amber-600" vacio="Nadie llegó tarde hoy.">
                    {r.tarde.map((p) => (
                        <Fila key={p.empleado_id} persona={p} detalle={p.departamento} derecha={
                            <div className="text-right shrink-0">
                                <p className="text-sm text-slate-700 tabular-nums">{horaAmPm(p.entrada)}</p>
                                <p className="text-xs font-medium text-amber-600 tabular-nums">{minutosTexto(p.retardo_min)} tarde</p>
                            </div>
                        } />
                    ))}
                </Lista>
                <Lista
                    titulo={yaEsHora ? 'No han marcado' : 'Todavía no llegan'} cantidad={r.sinMarcar.length}
                    tono={yaEsHora ? 'text-rose-500' : 'text-slate-400'} vacio="Ya marcaron todos."
                >
                    {r.sinMarcar.map((p) => (
                        <Fila key={p.empleado_id} persona={p} detalle={[p.departamento, p.sede].filter(Boolean).join(', ')} />
                    ))}
                </Lista>
                <Lista titulo="Fuera hoy" cantidad={datos.fuera.length} tono="text-emerald-600" vacio="Nadie de vacaciones ni de permiso hoy.">
                    {datos.fuera.map((i) => (
                        <Fila key={i.codigo} persona={i} detalle={fueraTexto(i)} derecha={
                            !i.aprobada && <span className="text-xs text-amber-600 shrink-0">{i.estado}</span>
                        } />
                    ))}
                </Lista>
                <section className={`${panel} p-5 flex flex-col min-h-[12rem]`}>
                    <header className="flex items-baseline justify-between gap-3 mb-3">
                        <h2 className="text-sm font-semibold text-slate-900">Marcajes en vivo</h2>
                        <span className="text-xs text-slate-400">
                            {(() => { const n = datos.aparatos.filter((a) => a.conectado).length; return `${n} ${n === 1 ? 'aparato' : 'aparatos'} en línea`; })()}
                        </span>
                    </header>
                    {datos.recientes.length === 0 ? (
                        <p className="text-sm text-slate-400 my-auto">Los marcajes aparecen aquí apenas alguien marca.</p>
                    ) : (
                        <ul className="-mx-2 space-y-0.5">
                            {datos.recientes.slice(0, 12).map((m) => (
                                <li key={m.id} className={`flex items-center gap-3 px-2 py-1.5 rounded-lg ${nuevos.has(m.id) ? 'recien-llegado' : ''}`}>
                                    <Avatar persona={m} size={32} />
                                    <div className="min-w-0 flex-1">
                                        {m.nombre
                                            ? <NombrePersona persona={m} className="block text-sm font-medium text-slate-900 truncate max-w-full" />
                                            : <p className="text-sm text-slate-500 truncate">Cédula {m.enroll_id}</p>}
                                        <p className="text-xs text-slate-400 truncate">{m.aparato}</p>
                                    </div>
                                    <span className="text-sm text-slate-600 tabular-nums shrink-0">{horaDeIso(m.fecha_hora)}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            </div>
        </div>
    );
};

export default HoyVista;
